import { useCallback, useEffect, useRef, useState } from 'react';
import { useAssistantRefresh } from './useAssistantRefresh';
import type { ListTasksQuery, TaskSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { taskApi } from '../api/task.api';
import type { BoardColumn } from '../components/TaskBoard';

/** How many cards a column shows before it needs a "Load more" click. */
export const COLUMN_BATCH = 20;

export interface ColumnState {
  items: TaskSummary[];
  /** Total matching rows on the server for this column - independent of how
   *  many are loaded, so a header can say "43 total" without loading all 43. */
  total: number;
  /** Highest page fetched so far (0 = nothing loaded yet). */
  page: number;
  loadingMore: boolean;
}

type Filters = Omit<ListTasksQuery, 'status' | 'page' | 'pageSize'>;

const EMPTY_COLUMN: ColumnState = { items: [], total: 0, page: 0, loadingMore: false };

/**
 * Loads a TaskBoard's columns independently (per the "Load more per column"
 * design): each column is its own small `status`-filtered, paginated query,
 * so a Pending column with 90 tasks never forces a 3-task Done column to
 * page along with it.
 *
 * `filters` is everything about the query EXCEPT status/page/pageSize - scope,
 * due-date bounds, importance, sort... the page owns that; this hook only
 * adds the per-column slicing on top, in parallel, one request per column.
 */
export function useColumnBoard(columns: BoardColumn[], filters: Filters) {
  const [state, setState] = useState<Record<string, ColumnState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Always-current mirror of `state`, so callbacks that must stay
  // referentially stable (loadMore, mutate) never read a stale snapshot.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // `filters` is a fresh object every render (pages pass an inline literal),
  // but its CONTENT rarely changes - stringify it so reload() only fires
  // when the query actually changed, not on every parent re-render.
  const filterKey = JSON.stringify(filters);
  const columnKey = columns.join(',');
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pages = await Promise.all(
        columns.map((status) =>
          taskApi.list({ ...filtersRef.current, status, page: 1, pageSize: COLUMN_BATCH }),
        ),
      );
      const next: Record<string, ColumnState> = {};
      columns.forEach((col, i) => {
        const res = pages[i];
        next[col] = {
          items: res.data,
          total: res.meta?.total ?? res.data.length,
          page: 1,
          loadingMore: false,
        };
      });
      setState(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load tasks');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKey, filterKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // The AI chat can change tasks server-side; refetch when it says so.
  useAssistantRefresh(reload);

  const loadMore = useCallback(async (column: BoardColumn) => {
    const current = stateRef.current[column] ?? EMPTY_COLUMN;
    if (current.loadingMore || current.items.length >= current.total) return;

    setState((s) => ({ ...s, [column]: { ...(s[column] ?? current), loadingMore: true } }));
    try {
      const nextPage = current.page + 1;
      const res = await taskApi.list({
        ...filtersRef.current,
        status: column,
        page: nextPage,
        pageSize: COLUMN_BATCH,
      });
      setState((s) => {
        const entry = s[column] ?? current;
        // Dedupe: a task nudged into this column by an optimistic mutate()
        // between page loads could otherwise reappear in the next batch.
        const seen = new Set(entry.items.map((t) => t.id));
        const fresh = res.data.filter((t) => !seen.has(t.id));
        return {
          ...s,
          [column]: {
            items: [...entry.items, ...fresh],
            total: res.meta?.total ?? entry.total,
            page: nextPage,
            loadingMore: false,
          },
        };
      });
    } catch (err) {
      setState((s) => {
        const entry = s[column] ?? EMPTY_COLUMN;
        return { ...s, [column]: { ...entry, loadingMore: false } };
      });
      setError(err instanceof ApiError ? err.message : 'Could not load more tasks');
    }
  }, []);

  /**
   * Folds a fresh/changed task into the right column, moving it out of
   * whichever column currently holds it first - the same "re-bucket by
   * current status" behaviour the old flat-array board had for free, just
   * done by hand now that each column is its own fetch.
   *
   * Pass `{ remove: true }` for a task that should leave the board entirely
   * (e.g. Important, when the star comes off) instead of relocating.
   */
  const mutate = useCallback(
    (updated: TaskSummary, opts?: { remove?: boolean }) => {
      setState((s) => {
        const next = { ...s };
        for (const col of columns) {
          const entry = next[col];
          if (entry && entry.items.some((t) => t.id === updated.id)) {
            next[col] = {
              ...entry,
              items: entry.items.filter((t) => t.id !== updated.id),
              total: Math.max(0, entry.total - 1),
            };
          }
        }
        if (opts?.remove) return next;

        const target = updated.status as BoardColumn;
        if (!columns.includes(target)) return next; // moved off-board (e.g. Cancelled on a work-only board)
        const entry = next[target] ?? EMPTY_COLUMN;
        next[target] = {
          ...entry,
          items: [updated, ...entry.items],
          total: entry.total + 1,
          page: entry.page || 1,
        };
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnKey],
  );

  const totalCount = columns.reduce((sum, col) => sum + (state[col]?.total ?? 0), 0);
  const allLoaded = columns.flatMap((col) => state[col]?.items ?? []);

  return {
    state,
    loading,
    error,
    reload,
    loadMore,
    mutate,
    /** Sum of each column's server-reported total - accurate even before
     *  every card is loaded, unlike counting what happens to be on screen. */
    totalCount,
    /** Every card currently loaded, across all columns - the best-effort
     *  data available for anything that needs to look inside a task (e.g. an
     *  assignee's response), which the server can't filter by column alone. */
    allLoaded,
  };
}
